import { PaneArea, ResizeByThumbWithTypeAndDifs } from '../../types/shared'
import twColors from '../constants/colors'
import { CanvasElementType, TextBaseline } from '../constants/common'
import { getRoundedObject } from '../controllers/renderUtils'
import PaneModel from '../models/pane'
import { convertColorToHEX, getHEXDarkenedColor, getHEXWithSpecificAplha } from '../utils/colors'
import { isPointInBox } from '../utils/helpers'
import Element from './element'
import Text, { ITextAttrs } from './text'
import Thumb from './thumb'

interface Coordinates {
  x1: number
  x2: number
  y1: number
  y2: number
}

export interface IPositionAttrs extends Coordinates {
  targetColor: string
  stopColor: string
  targetY: number
  centerY: number
  stopY: number
}

class Position<Attrs extends IPositionAttrs = IPositionAttrs> extends Element<Attrs> {
  static type = CanvasElementType.position

  name = 'Position'

  declare scaled: Pick<Attrs, 'x1' | 'x2' | 'y1' | 'y2' | 'targetY' | 'centerY' | 'targetY' | 'stopY'>

  x1Dif: number
  x2Dif: number
  direction: number
  targetLabelBox = { x: 0, y: 0, width: 0, height: 0 }
  stopLabelBox = { x: 0, y: 0, width: 0, height: 0 }

  constructor(values: Partial<Attrs>, model: PaneModel) {
    super(values, model)
    this.resize = this.resize.bind(this)
    this.direction = this.attrs.targetY > this.attrs.centerY ? 1 : -1
    this._thumbs = [
      new Thumb(
        'c1',
        () => this.attrs.x1,
        () => this.attrs.centerY,
        this.resize,
        this.model
      ),
      new Thumb(
        'c2',
        () => this.attrs.x2,
        () => this.attrs.centerY,
        this.resize,
        this.model
      ),
      new Thumb(
        't1',
        () => this.attrs.x1,
        () => this.attrs.targetY,
        this.resize,
        this.model
      ),
      new Thumb(
        't2',
        () => this.attrs.x2,
        () => this.attrs.targetY,
        this.resize,
        this.model
      ),
      new Thumb(
        's1',
        () => this.attrs.x1,
        () => this.attrs.stopY,
        this.resize,
        this.model
      ),
      new Thumb(
        's2',
        () => this.attrs.x2,
        () => this.attrs.stopY,
        this.resize,
        this.model
      ),
    ]
    this.scale(this.getBoundingPointKeys())
    this.x1Dif = this.x2Dif = 0
  }

  getBoundingPointKeys = () => ({ x: ['x1', 'x2'], y: ['targetY', 'centerY', 'stopY'] })

  renderContent(context: CanvasRenderingContext2D) {
    const { x1, x2, targetY, centerY, stopY } = this.scaled
    const roundedXY = getRoundedObject({ x1, x2, targetY, centerY, stopY })
    const width = roundedXY.x2 - roundedXY.x1

    context.set('fillStyle', this.attrs.targetColor)
    context.fillRect(roundedXY.x1, roundedXY.targetY, width, roundedXY.centerY - roundedXY.targetY)

    context.set('fillStyle', this.attrs.stopColor)
    context.fillRect(roundedXY.x1, roundedXY.centerY, width, roundedXY.stopY - roundedXY.centerY)

    const targetDif = Math.abs(this.attrs.targetY - this.attrs.centerY)
    const targetPerc = (100 / this.attrs.centerY) * targetDif
    const targetStr = `Target: ${targetDif.toFixed(2)} (${targetPerc.toFixed(2)}%)`

    const stopDif = Math.abs(this.attrs.stopY - this.attrs.centerY)
    const stopPerc = (100 / this.attrs.centerY) * stopDif
    const stopStr = `Stop: ${stopDif.toFixed(2)} (${stopPerc.toFixed(2)}%)`

    const sign = this.attrs.targetY > this.attrs.stopY ? 1 : -1

    // NOTE: this is used to correctly offset the labels from the rectangle
    // there is slight difference in padding of background of the labels this is hack to match exact spacing
    const targetOffset = sign > 0 ? 2 : 0
    const stopOffset = sign > 0 ? 0 : 2
    const labelSpacing = 17

    const text = new Text(
      {
        text: targetStr,
        fillStyle: 'white',
        font: { size: 9, family: 'Finviz Sans, Inter Fallback', weight: '500' },
        lineHeight: 9,
        y: roundedXY.targetY - sign * labelSpacing + targetOffset,
        textBaseline: TextBaseline.middle,
        background: getHEXDarkenedColor(convertColorToHEX(this.attrs.targetColor)),
        padding: { top: 4, right: 4, bottom: 4, left: 4 },
      },
      this.model
    )
    let textWidth = text.measure(context)
    const targetLabelX = roundedXY.x1 + (width - textWidth) / 2 - text.attrs.padding.left
    const targetLabelY = roundedXY.targetY - sign * labelSpacing + targetOffset
    text.set({ x: targetLabelX } as Partial<ITextAttrs>)

    // Store target label box dimensions for hit detection
    const targetBoxWidth = textWidth + text.attrs.padding.left + text.attrs.padding.right
    const targetBoxHeight = text.attrs.lineHeight + text.attrs.padding.top + text.attrs.padding.bottom
    this.targetLabelBox = {
      x: targetLabelX,
      y: targetLabelY - targetBoxHeight / 2,
      width: targetBoxWidth,
      height: targetBoxHeight,
    }

    text.render(context)

    const stopLabelY = roundedXY.stopY + sign * 17 + stopOffset
    text.set({
      text: stopStr,
      y: stopLabelY,
      textBaseline: TextBaseline.middle,
      background: getHEXDarkenedColor(convertColorToHEX(this.attrs.stopColor)),
    })
    textWidth = text.measure(context)
    const stopLabelX = roundedXY.x1 + (width - textWidth) / 2 - text.attrs.padding.left
    text.set({ x: stopLabelX } as Partial<ITextAttrs>)

    // Store stop label box dimensions for hit detection
    const stopBoxWidth = textWidth + text.attrs.padding.left + text.attrs.padding.right
    const stopBoxHeight = text.attrs.lineHeight + text.attrs.padding.top + text.attrs.padding.bottom
    this.stopLabelBox = {
      x: stopLabelX,
      y: stopLabelY - stopBoxHeight / 2,
      width: stopBoxWidth,
      height: stopBoxHeight,
    }

    text.render(context)

    if (this.getShouldRenderThumbs()) {
      this.renderThumbs(context)
    }
  }

  addToCoordinates(difs: Partial<IPositionAttrs>) {
    if (difs.centerY) {
      this.attrs.centerY += difs.centerY
    }

    if (difs.targetY) {
      this.attrs.targetY += difs.targetY
    }

    if (difs.stopY) {
      this.attrs.stopY += difs.stopY
    }

    const isTargetSwapped =
      this.direction === 1 ? this.attrs.targetY < this.attrs.centerY : this.attrs.targetY > this.attrs.centerY
    const isStopSwapped =
      this.direction === 1 ? this.attrs.stopY > this.attrs.centerY : this.attrs.stopY < this.attrs.centerY

    if (isTargetSwapped || isStopSwapped) {
      this.direction *= -1

      if (isTargetSwapped)
        this.attrs.stopY = this.attrs.centerY - Math.abs(this.attrs.stopY - this.attrs.centerY) * this.direction

      if (isStopSwapped)
        this.attrs.targetY = this.attrs.centerY + Math.abs(this.attrs.targetY - this.attrs.centerY) * this.direction
    }

    this.x1Dif += difs.x1 || 0
    this.x2Dif += difs.x2 || 0
    const roundedDifX1 = ~~this.x1Dif
    const roundedDifX2 = ~~this.x2Dif

    if (roundedDifX1 !== 0 || roundedDifX2 !== 0) {
      if (difs.x1 != null) {
        this.attrs.x1 += roundedDifX1
      }
      if (difs.x2 != null) {
        this.attrs.x2 += roundedDifX2
      }

      this.x1Dif -= roundedDifX1
      this.x2Dif -= roundedDifX2
    }
  }

  moveBy(x: number, y: number) {
    return this.addToCoordinates({
      x1: x,
      x2: x,
      targetY: y,
      centerY: y,
      stopY: y,
    })
  }

  resize({ type, difX, difY: difYOriginal, area }: ResizeByThumbWithTypeAndDifs) {
    const [coordType, coordIndex] = (type as string).split('')
    const yPoints: Record<string, keyof IPositionAttrs> = { t: 'targetY', c: 'centerY', s: 'stopY' }
    const coordValue = yPoints[coordType]

    const obj: Partial<IPositionAttrs> = {
      [`x${coordIndex}`]: difX,
      [coordValue]: difYOriginal,
    }

    const isDraggingCenter = obj.centerY !== undefined && !obj.targetY && !obj.stopY
    if (isDraggingCenter) {
      let difY = difYOriginal
      const boundaryTop = Math.max(this.attrs.targetY, this.attrs.stopY)
      const boundaryBottom = Math.min(this.attrs.targetY, this.attrs.stopY)
      const prevMouseY = area.y - difY
      if (Math.sign(difY) === -1 && prevMouseY > boundaryTop) {
        difY = area.y - boundaryTop
      } else if (Math.sign(difY) === 1 && prevMouseY < boundaryBottom) {
        difY = area.y - boundaryBottom
      }

      const newCenterY = this.attrs.centerY + difY
      const isTargetInRange = this.direction === 1 ? newCenterY < this.attrs.targetY : newCenterY > this.attrs.targetY
      const isStopInRange = this.direction === 1 ? newCenterY > this.attrs.stopY : newCenterY < this.attrs.stopY
      if (!isTargetInRange) {
        difY = Math.abs(this.attrs.targetY - this.attrs.centerY) * this.direction
      } else if (!isStopInRange) {
        difY = Math.abs(this.attrs.stopY - this.attrs.centerY) * -this.direction
      }

      ;(obj[coordValue] as number) = difY
    }

    return this.addToCoordinates(obj)
  }

  isInArea(area: PaneArea) {
    if (super.isDrawingElementLockedOrInvisible()) return false

    // Check main rectangles in unscaled coordinates
    const x1 = Math.round(this.attrs.x1)
    const x2 = Math.round(this.attrs.x2)
    const left = Math.min(x1, x2)
    const right = Math.max(x1, x2)
    const top = Math.min(this.attrs.targetY, this.attrs.stopY)
    const bottom = Math.max(this.attrs.targetY, this.attrs.stopY)
    if (left < area.x && area.x < right && top < area.y && area.y < bottom) {
      return true
    }

    // Check target label box using stored dimensions from renderContent
    if (isPointInBox(area.scaled, this.targetLabelBox)) {
      return true
    }

    // Check stop label box using stored dimensions from renderContent
    if (isPointInBox(area.scaled, this.stopLabelBox)) {
      return true
    }

    return super.isInArea(area)
  }

  onMouseUp(paneArea?: PaneArea) {
    super.onMouseUp(paneArea)
    this.x1Dif = this.x2Dif = 0
  }
}

Position.prototype.defaults = {
  targetColor: getHEXWithSpecificAplha(twColors.green[400], 0.15),
  stopColor: getHEXWithSpecificAplha(twColors.red[400], 0.15),
}

Position.prototype.modalConfig = {
  inputs: [
    { type: 'background', name: 'targetColor', label: 'Target' },
    { type: 'background', name: 'stopColor', label: 'Stop' },
  ],
}

export default Position
